import React, { useEffect, useRef } from 'react'

export interface AudioClip {
  id: string
  url: string
  start: number
  duration: number
  gain_db?: number
  offset?: number
}

interface Props {
  clips: AudioClip[]
  currentTime: number
  isPlaying: boolean
  muted?: boolean
}

// HTMLAudioElement.volume is capped at 1.0, so preview can attenuate but not
// boost past 0 dB. The export path applies the real (boostable) gain.
const dbToVolume = (db?: number) => Math.max(0, Math.min(1, Math.pow(10, (db ?? 0) / 20)))

const SEEK_THRESHOLD = 0.25 // s — only re-seek an element when drift exceeds this

/**
 * Hidden multi-track audio mixer for the Studio preview. Renders one <audio>
 * per timeline clip (music / voice / SFX) and keeps each one aligned to the
 * shared timeline clock so they play *over* the base video during preview.
 */
export function AudioMixer({ clips, currentTime, isPlaying, muted }: Props) {
  const els = useRef<Record<string, HTMLAudioElement | null>>({})

  useEffect(() => {
    for (const c of clips) {
      const a = els.current[c.id]
      if (!a) continue
      const end = c.start + c.duration
      const inWindow = currentTime >= c.start - 0.05 && currentTime < end
      a.volume = muted ? 0 : dbToVolume(c.gain_db)
      const target = Math.max(0, currentTime - c.start + (c.offset || 0))

      if (inWindow && isPlaying) {
        if (Math.abs(a.currentTime - target) > SEEK_THRESHOLD) {
          try { a.currentTime = target } catch { /* not seekable yet */ }
        }
        if (a.paused) a.play().catch(() => { /* autoplay/gesture guard */ })
      } else {
        if (!a.paused) a.pause()
        // Keep a scrubbed-but-paused clip aligned so the next Play is instant.
        if (inWindow && Math.abs(a.currentTime - target) > SEEK_THRESHOLD * 1.5) {
          try { a.currentTime = target } catch { /* ignore */ }
        }
      }
    }
  }, [clips, currentTime, isPlaying, muted])

  // Belt-and-suspenders: silence everything on unmount.
  useEffect(() => () => {
    Object.values(els.current).forEach(a => { try { a?.pause() } catch { /* ignore */ } })
  }, [])

  return (
    <div aria-hidden style={{ display: 'none' }}>
      {clips.map(c => (
        <audio
          key={c.id}
          ref={el => { els.current[c.id] = el }}
          src={c.url}
          preload="auto"
        />
      ))}
    </div>
  )
}
